import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import DashboardHeader from './components/DashboardHeader';
import QuickStatsCards from './components/QuickStatsCards';
import FilterBar from './components/FilterBar';
import ContractGrid from './components/ContractGrid';
import ContractList from './components/ContractList';

const ContractDashboard = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    attorney: '',
    dateRange: '',
    minValue: '',
    maxValue: '',
    client: '',
    contractId: '',
    startDate: '',
    endDate: '',
  });

  // Mock contract data
  const mockContracts = [
    {
      id: 'CT-2024-001',
      contractId: 'CT-2024-001',
      title: 'Master Service Agreement',
      type: 'Service Agreement',
      status: 'Active',
      client: 'TechCorp Solutions Inc.',
      assignedAttorney: 'Sarah Johnson',
      startDate: '2024-01-15',
      expirationDate: '2025-01-14',
      value: '$2,500,000',
      lastModified: '2024-03-20',
      documentCount: 12,
    },
    {
      id: 'CT-2024-002',
      contractId: 'CT-2024-002',
      title: 'Software Licensing Agreement',
      type: 'Licensing Agreement',
      status: 'Pending',
      client: 'Digital Innovations LLC',
      assignedAttorney: 'Michael Chen',
      startDate: '2024-02-01',
      expirationDate: '2025-02-01',
      value: '$1,800,000',
      lastModified: '2024-03-18',
      documentCount: 8,
    },
    {
      id: 'CT-2024-003',
      contractId: 'CT-2024-003',
      title: 'Employment Contract - Senior Developer',
      type: 'Employment Contract',
      status: 'Active',
      client: 'Internal HR Department',
      assignedAttorney: 'Emily Davis',
      startDate: '2024-01-10',
      expirationDate: '2025-01-10',
      value: '$150,000',
      lastModified: '2024-03-15',
      documentCount: 5,
    },
    {
      id: 'CT-2024-004',
      contractId: 'CT-2024-004',
      title: 'Non-Disclosure Agreement',
      type: 'NDA',
      status: 'Expired',
      client: 'StartupX Ventures',
      assignedAttorney: 'Robert Wilson',
      startDate: '2023-06-01',
      expirationDate: '2024-06-01',
      value: '$0',
      lastModified: '2024-06-02',
      documentCount: 3,
    },
    {
      id: 'CT-2024-005',
      contractId: 'CT-2024-005',
      title: 'Vendor Supply Agreement',
      type: 'Vendor Agreement',
      status: 'Active',
      client: 'Global Manufacturing Co.',
      assignedAttorney: 'Lisa Anderson',
      startDate: '2024-03-01',
      expirationDate: '2024-12-31',
      value: '$3,200,000',
      lastModified: '2024-03-25',
      documentCount: 15,
    },
    {
      id: 'CT-2024-006',
      contractId: 'CT-2024-006',
      title: 'Consulting Services Agreement',
      type: 'Service Agreement',
      status: 'Draft',
      client: 'Enterprise Solutions Group',
      assignedAttorney: 'Sarah Johnson',
      startDate: '2024-04-01',
      expirationDate: '2025-03-31',
      value: '$950,000',
      lastModified: '2024-03-28',
      documentCount: 4,
    },
  ];

  // Mock statistics
  const mockStats = {
    totalContracts: 156,
    activeContracts: 89,
    expiringSoon: 12,
    totalValue: '$24.8M',
  };

  // Filter contracts based on current filters
  const filteredContracts = mockContracts?.filter(contract => {
    if (filters?.status && contract?.status?.toLowerCase() !== filters?.status) return false;
    if (filters?.type && contract?.type?.toLowerCase()?.replace(/\s+/g, '-') !== filters?.type) return false;
    if (filters?.attorney && contract?.assignedAttorney?.toLowerCase()?.replace(/\s+/g, '-') !== filters?.attorney) return false;
    if (filters?.client && !contract?.client?.toLowerCase()?.includes(filters?.client?.toLowerCase())) return false;
    if (filters?.contractId && !contract?.contractId?.toLowerCase()?.includes(filters?.contractId?.toLowerCase())) return false;
    
    return true;
  });

  // Count active filters
  const activeFiltersCount = Object.values(filters)?.filter(value => value !== '')?.length;

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      type: '',
      attorney: '',
      dateRange: '',
      minValue: '',
      maxValue: '',
      client: '',
      contractId: '',
      startDate: '',
      endDate: '',
    });
  };

  const handleSelectContract = (contractId, isSelected) => {
    if (isSelected) {
      setSelectedContracts(prev => [...prev, contractId]);
    } else {
      setSelectedContracts(prev => prev?.filter(id => id !== contractId));
    }
  };

  const handleQuickAction = (action, contract) => {
    console.log(`${action} action for contract:`, contract?.contractId);
    // Handle quick actions like edit, duplicate, archive, etc.
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk ${action} for contracts:`, selectedContracts);
    // Handle bulk actions
  };

  const handleNewContract = () => {
    console.log('Creating new contract');
    // Navigate to contract creation
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Dashboard Header */}
          <DashboardHeader
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onNewContract={handleNewContract}
            selectedContracts={selectedContracts}
            onBulkAction={handleBulkAction}
            totalContracts={mockContracts?.length}
            filteredContracts={filteredContracts?.length}
          />

          {/* Quick Stats */}
          <QuickStatsCards stats={mockStats} />

          {/* Filter Bar */}
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            activeFiltersCount={activeFiltersCount}
          />

          {/* Contract Display */}
          {viewMode === 'grid' ? (
            <ContractGrid
              contracts={filteredContracts}
              onQuickAction={handleQuickAction}
              selectedContracts={selectedContracts}
              onSelectContract={handleSelectContract}
            />
          ) : (
            <ContractList
              contracts={filteredContracts}
              onQuickAction={handleQuickAction}
              selectedContracts={selectedContracts}
              onSelectContract={handleSelectContract}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ContractDashboard;